// ===================================================
// src/components/Planning/AdjustPercentagesModal.js
// 🧮 Adjust Sales / Production / M-L Splits + Amazon Share Split (with regional totals)
// ===================================================
import React, { useState, useEffect } from "react";
import { X, Save } from "lucide-react";

const GROUPS = [
  { key: "us_cityplay", label: "🇺🇸 CityPlay" },
  { key: "us_20", label: "🇺🇸 2.0" },
  { key: "uk_cityplay", label: "🇬🇧 CityPlay" },
  { key: "uk_20", label: "🇬🇧 2.0" },
];

const AdjustPercentagesModal = ({ onClose }) => {
  const [tab, setTab] = useState("sales");
  const [sales, setSales] = useState({});
  const [production, setProduction] = useState({});
  const [mlSales, setMlSales] = useState({});
  const [mlProduction, setMlProduction] = useState({});
  const [amazonShare, setAmazonShare] = useState({ total: 0.3, split: {} });
  const [loading, setLoading] = useState(true);

  // 🧮 Load from DB
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/plan/percentages");
        const data = await res.json();
        if (!data.success) throw new Error("Failed to load percentages");

        setSales(data.sales || {});
        setProduction(data.production || {});
        setMlSales(data.ml_sales || {});
        setMlProduction(data.ml_production || {});

        const amazon =
          typeof data.amazon_share === "object"
            ? data.amazon_share
            : { total: Number(data.amazon_share) || 0.3, split: {} };
        setAmazonShare(amazon);
      } catch (err) {
        console.error("❌ Load failed:", err);
        alert("Failed to load percentages");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (section, key, value) => {
    const setter = section === "sales" ? setSales : setProduction;
    const state = section === "sales" ? sales : production;
    setter({
      ...state,
      [key]: parseFloat(value) || 0,
    });
  };

  const totalSales = Object.entries(sales)
    .filter(([k]) => !k.endsWith("_l") && !k.endsWith("_m"))
    .reduce((a, [, b]) => a + b, 0);

  const totalProd = Object.entries(production)
    .filter(([k]) => !k.endsWith("_l") && !k.endsWith("_m"))
    .reduce((a, [, b]) => a + b, 0);

  // 🚀 Save to DB
  const handleSave = async () => {
    try {
      const payload = {
        sales,
        production,
        ml_sales: mlSales,
        ml_production: mlProduction,
        amazon_share: amazonShare,
      };

      const res = await fetch("/api/plan/percentages", {
        method: "put",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save settings");

      alert("✅ Percentages updated successfully!");
      onClose();
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Failed to save percentages");
    }
  };

  const input = {
    width: "80px",
    padding: "4px 6px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    textAlign: "center",
  };

  const inputSmall = {
    width: "60px",
    padding: "3px 5px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    textAlign: "center",
  };

  const groupBox = {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "0.75rem",
    marginBottom: "1rem",
    background: "#f9fafb",
  };

  // 💰 Sales Section
  const renderSalesSection = () => (
    <div>
      <h4 style={{ fontWeight: 600 }}>
        💰 Sales total:{" "}
        <span style={{ color: totalSales === 100 ? "green" : "red" }}>
          {totalSales}%
        </span>
      </h4>

      {GROUPS.map(({ key, label }) => (
        <div key={key} style={groupBox}>
          <label style={{ fontWeight: 600 }}>{label}</label>
          <input
            type="number"
            style={input}
            value={sales[key] || 0}
            onChange={(e) => handleChange("sales", key, e.target.value)}
          />

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <div>
              <label style={{ fontSize: "0.8rem" }}>M (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                style={inputSmall}
                value={mlSales[key]?.m || 0}
                onChange={(e) =>
                  setMlSales((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      m: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
              />
            </div>

            <div>
              <label style={{ fontSize: "0.8rem" }}>L (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                style={inputSmall}
                value={mlSales[key]?.l || 0}
                onChange={(e) =>
                  setMlSales((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      l: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // 🏭 Production Section
  const renderProductionSection = () => (
    <div>
      <h4 style={{ fontWeight: 600 }}>
        🏭 Production total:{" "}
        <span style={{ color: totalProd === 100 ? "green" : "red" }}>
          {totalProd}%
        </span>
      </h4>

      {GROUPS.map(({ key, label }) => (
        <div key={key} style={groupBox}>
          <label style={{ fontWeight: 600 }}>{label}</label>
          <input
            type="number"
            style={input}
            value={production[key] || 0}
            onChange={(e) => handleChange("production", key, e.target.value)}
          />

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <div>
              <label style={{ fontSize: "0.8rem" }}>M (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                style={inputSmall}
                value={mlProduction[key]?.m || 0}
                onChange={(e) =>
                  setMlProduction((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      m: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
              />
            </div>

            <div>
              <label style={{ fontSize: "0.8rem" }}>L (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                style={inputSmall}
                value={mlProduction[key]?.l || 0}
                onChange={(e) =>
                  setMlProduction((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      l: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading)
    return (
      <div style={overlay}>
        <div style={modal}>Loading percentages...</div>
      </div>
    );

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h3>⚙️ Adjust Percentages</h3>
          <button onClick={onClose} style={closeBtn}>
            <X size={20} />
          </button>
        </div>

        {/* 🛒 Amazon Share */}
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 1rem",
            background: "#f3f4f6",
            borderRadius: "8px",
          }}
        >
          <label style={{ fontWeight: 600 }}>🛒 Amazon Share (% of Sales)</label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginTop: "0.5rem",
            }}
          >
            <input
              type="range"
              min="0"
              max="100"
              value={(amazonShare?.total || 0) * 100}
              onChange={(e) =>
                setAmazonShare((prev) => ({
                  ...prev,
                  total: parseFloat(e.target.value) / 100,
                }))
              }
              style={{ flex: 1 }}
            />
            <span
              style={{
                minWidth: "3rem",
                textAlign: "right",
                fontWeight: 600,
              }}
            >
              {((amazonShare?.total || 0) * 100).toFixed(0)}%
            </span>
          </div>

          <div style={{ textAlign: "right", marginTop: "0.5rem" }}>
            <button
              onClick={() =>
                setAmazonShare((prev) => ({
                  ...prev,
                  showSplit: !prev.showSplit,
                }))
              }
              style={{
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "0.3rem 0.75rem",
                cursor: "pointer",
                fontSize: "0.85rem",
              }}
            >
              ⚙️ {amazonShare?.showSplit ? "Hide Split" : "Adjust Amazon Split"}
            </button>
          </div>

          {amazonShare?.showSplit && (
            <div
              style={{
                marginTop: "1rem",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "0.75rem",
              }}
            >
              {["us", "uk"].map((region) => (
                <div
                  key={region}
                  style={{
                    marginBottom: "1.25rem",
                    borderBottom: "1px solid #f3f4f6",
                    paddingBottom: "0.75rem",
                  }}
                >
                  <h5 style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                    {region === "us" ? "🇺🇸 Amazon US" : "🇬🇧 Amazon UK"}
                  </h5>

                  {/* ✅ Regional total */}
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                    <label style={{ width: "110px", fontSize: "0.85rem", fontWeight: 500 }}>
                      Total (%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={(amazonShare?.split?.[region]?.total || 0) * 100}
                      onChange={(e) =>
                        setAmazonShare((prev) => ({
                          ...prev,
                          split: {
                            ...prev.split,
                            [region]: {
                              ...prev.split?.[region],
                              total: parseFloat(e.target.value) / 100,
                            },
                          },
                        }))
                      }
                      style={{ flex: 1 }}
                    />
                    <span style={{ width: "3rem", textAlign: "right", fontWeight: 600 }}>
                      {((amazonShare?.split?.[region]?.total || 0) * 100).toFixed(0)}%
                    </span>
                  </div>

                  {["cityplay", "2_0"].map((product) => (
                    <div key={product} style={{ marginLeft: "1rem", marginBottom: "0.5rem" }}>
                      <label style={{ fontWeight: 500 }}>
                        {product === "cityplay" ? "CityPlay" : "2.0"}
                      </label>
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
                        {["m", "l"].map((size) => (
                          <div key={size}>
                            <label style={{ fontSize: "0.8rem" }}>{size.toUpperCase()}</label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.01"
                              value={
                                amazonShare?.split?.[region]?.[product]?.[size] ?? ""
                              }
                              onChange={(e) =>
                                setAmazonShare((prev) => ({
                                  ...prev,
                                  split: {
                                    ...prev.split,
                                    [region]: {
                                      ...prev.split?.[region],
                                      [product]: {
                                        ...prev.split?.[region]?.[product],
                                        [size]: parseFloat(e.target.value) || 0,
                                      },
                                    },
                                  },
                                }))
                              }
                              style={{
                                width: "60px",
                                marginLeft: "0.4rem",
                                textAlign: "center",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "3px 5px",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <button
            onClick={() => setTab("sales")}
            style={{
              ...tabButton,
              backgroundColor: tab === "sales" ? "#3b82f6" : "#e5e7eb",
              color: tab === "sales" ? "white" : "#111",
            }}
          >
            Sales
          </button>
          <button
            onClick={() => setTab("production")}
            style={{
              ...tabButton,
              backgroundColor: tab === "production" ? "#3b82f6" : "#e5e7eb",
              color: tab === "production" ? "white" : "#111",
            }}
          >
            Production
          </button>
        </div>

        {tab === "sales" && renderSalesSection()}
        {tab === "production" && renderProductionSection()}

        <div style={{ textAlign: "right", marginTop: "1rem" }}>
          <button
            onClick={handleSave}
            style={{
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <Save size={16} style={{ marginRight: "0.4rem" }} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Styles =====
const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};

const modal = {
  background: "white",
  padding: "1.5rem 2rem",
  borderRadius: "10px",
  width: "700px",
  boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  overflowY: "auto",
  maxHeight: "90vh",
};

const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "0.5rem",
};

const closeBtn = { background: "none", border: "none", cursor: "pointer" };

const tabButton = {
  flex: 1,
  padding: "0.5rem",
  border: "none",
  borderRadius: "6px",
  fontWeight: 600,
  cursor: "pointer",
};

export default AdjustPercentagesModal;
